/** @jsx jsx */
import { jsx } from '@emotion/core';

import {
  Box,
  FormHelperText,
  Grid,
  Theme,
  Typography,
  styled,
  useTheme,
} from '@material-ui/core';
import { RouteComponentProps, useLocation } from '@reach/router';
import * as queryString from 'query-string';
import React, { Fragment, useEffect, useState } from 'react';
import colors from '../../../../../themes/main-colors';
import { ProgramCard } from '../../../../pge-plus-common/ProgramCard';
import microcopyGroupIds from '../../../../pge-plus-common/microcopyGroupIds';

import {
  AccountDetail,
  AccountDetailList,
  AccountDetailParams,
  AccountRelationship,
} from '../../../../../__generated__/pge-types';
import useERApplicationApi, {
  getApplicationsByAccountWithAddressQuery,
} from '../../../../../hooks/pge-plus/useERApplicationApi';
import useAccountCustomer from '../../../../../hooks/useAccountCustomer';
import useAccountDetailList from '../../../../../hooks/useAccountDetailList';
import useContentMgmt from '../../../../../hooks/useContentMgmt';
import useWrapWithLoader from '../../../../../hooks/useWrapWithLoading';
import AccountSearch from '../../../../account-search/AccountSearch';
import useAccountListHeader from '../../../../account-summary/multi-accounts/useAccountListHeader';
import Backdrop from '../../../../backdrop';
import {
  getAccounts,
  getAccountDetails,
} from '../../../../pge-plus-common/queries/ChooseAccount';
import useUtil from '../../../../../hooks/pge-plus/useUtil';
import { useRebateOnlyFormState } from '../../../../../providers/RebateOnlyFormStateProvider';
import {
  Application,
  ApplicationEr,
  ApplicationErSectionType,
  ApplicationStatus,
  ApplicationType,
  IneligibilityReasonCode,
  SectionEr,
  SectionErAccountInfo,
  SectionErRebateInfo,
  SectionErRebateInfoData,
} from '../../../../../__generated__/pgeplus-types';
import { navigate } from 'gatsby';
import routes from '../../../../../routes';
import { Details } from '@material-ui/icons';
import useAuthQuery from '../../../../../hooks/useAuthQuery';
import { IneligibilityTypeNumber } from '../../../../pge-plus-form/Ineligible';

const ITEMS_PER_PAGE = 6;
const PREFIX = 'ERChargerRebateOnlyChooseAccount';
const classes = {
  accountCard: `${PREFIX}-accountCard`,
  showMoreAccounts: `${PREFIX}-showMoreAccounts`,
  cardSearch: `${PREFIX}-cardSearch`,
};

type QueryParams = {
  showOnlyInProgressAcc?: string;
};

const StyledGrid = styled(Grid)(({ theme }: { theme: Theme }) => ({
  [`& .${classes.accountCard}`]: {
    padding: theme.spacing(3, 3),
    minHeight: '125px',
    display: 'flex',
    alignItems: 'center',
  },
  [`& .${classes.showMoreAccounts}`]: {
    fontSize: '18px',
    lineHeight: '30px',
    color: colors.sparkBlue,
    textAlign: 'center',
    cursor: 'pointer',
    fontWeight: '700',
  },
  [`& .${classes.cardSearch}`]: {
    marginBottom: '5px',
    zIndex: 100,
  },
}));

const ChooseAccount: React.FC<RouteComponentProps> = (
  props: RouteComponentProps,
) => {
  const theme = useTheme();
  const { content } = useContentMgmt(
    microcopyGroupIds.PGE_PLUS_REBATE_ONLY_CHOOSE_ACCOUNT,
  );
  const location = useLocation();
  const queryParams = queryString.parse(location.search) as QueryParams;

  const { accountListParams, onAccountSearch } = useAccountListHeader();
  const [showAccountByLimit, setShowAccountByLimit] = useState(ITEMS_PER_PAGE);
  const [inProgressApplications, setInProgressApplications] = useState<
    Application[]
  >([]);

  const shouldShowInProgressAccOnly =
    queryParams.showOnlyInProgressAcc === 'true';

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const { customer, loading: loadingCustomer } = useAccountCustomer();

  const { accounts, totalCount, loading } = useAccountDetailList(
    getAccounts,
    'RES_ACCTS',
    {
      accountListParams: {
        ...accountListParams,
        pageSize: showAccountByLimit,
        userAccountRelFilter: [
          AccountRelationship.Main,
          AccountRelationship.Coapp,
        ],
      },
    },
  );

  const accountNumbers = inProgressApplications.map(application => ({
    accountNumber: application.accountId,
  }));

  const {
    data: accountDetailsList,
    loading: accountDetailsLoading,
  } = useAuthQuery<
    {
      getAccountDetails: Array<AccountDetail>;
      getAccountDetailList: AccountDetailList;
    },
    {
      params: AccountDetailParams;
    }
  >(getAccountDetails, {
    skip: !Boolean(accountNumbers.length) || !shouldShowInProgressAccOnly,
    variables: {
      params: {
        accountNumberList: accountNumbers.map(({ accountNumber }) => ({
          accountNumber,
        })),
        paging: {
          limit: showAccountByLimit,
          offset: 0,
        },
      },
    },
    onCompleted: data => {
      const accountDetails = data?.getAccountDetails;
      if (
        shouldShowInProgressAccOnly &&
        accountDetails?.length === 1 &&
        accountDetails[0] &&
        accountDetails[0].premiseInfo &&
        accountDetails[0].premiseInfo[0] &&
        accountDetails[0].premiseInfo[0].encryptedPremiseId
      ) {
        void handleSelectedAccount(
          accountDetails[0],
          accountDetails[0].premiseInfo[0].encryptedPremiseId,
          getPendingApplicationData(
            accountDetails[0].accountNumber,
            accountDetails[0].premiseInfo[0].addressLine1,
            inProgressApplications,
          ),
        );
      }
    },
  });

  const accountsList =
    (shouldShowInProgressAccOnly
      ? accountDetailsList?.getAccountDetails
      : accounts) || [];

  const {
    updateFormState,
    initFormStateAndNavigate,
  } = useRebateOnlyFormState();
  const {
    createApplication,
    getApplicationDetails,
    getApplicationsByAccountWithDetails,
  } = useERApplicationApi();
  const { wrapWithLoader } = useWrapWithLoader();
  const { setErrorNotification } = useUtil();

  useEffect(() => {
    if (shouldShowInProgressAccOnly) {
      void getInProgressApplications();
    }
  }, []);

  useEffect(() => {
    if (
      !shouldShowInProgressAccOnly &&
      !loading &&
      accounts.length === 0 &&
      !accountListParams.query
    ) {
      void navigate(routes.PGE_PLUS_REBATE_ONLY_INELIGIBLE, {
        state: {
          message: 'INELIGIBLE_DUE_TO_KNOWN_REASON',
          showBackButton: false,
        },
      });
    }

    if (!shouldShowInProgressAccOnly && accounts.length > 0 && customer) {
      void getInProgressApplications();
    }
  }, [accounts.length, customer, loading]);

  const getInProgressApplications = wrapWithLoader(async () => {
    try {
      const variables: any = {
        applicationType: ApplicationType.Er,
        status: [
          ApplicationStatus.Created,
          ApplicationStatus.InProgress,
          ApplicationStatus.Submitted,
          ApplicationStatus.Servicing,
        ],
      };

      if (!shouldShowInProgressAccOnly) {
        const accountNumbersToInclude = accountsList.map(
          account => account.accountNumber,
        );
        variables['accountId'] = accountNumbersToInclude;
      }

      const { data } = await getApplicationsByAccountWithDetails(
        variables,
        getApplicationsByAccountWithAddressQuery,
      );

      //Auto select single account
      if (
        !shouldShowInProgressAccOnly &&
        accountListParams.query === '' &&
        totalCount === 1 &&
        accountsList[0].premiseInfo &&
        accountsList[0].premiseInfo?.length === 1 &&
        accountsList[0].premiseInfo[0] &&
        customer?.personId
      ) {
        void handleSelectedAccount(
          accountsList[0],
          accountsList[0].premiseInfo[0].encryptedPremiseId,
          getPendingApplicationData(
            accountsList[0].accountNumber,
            accountsList[0].premiseInfo[0].addressLine1,
            data.getApplications.applications,
          ),
          /* isAccountHasPendingApplication(
            accounts[0].accountNumber,
            accounts[0].premiseInfo[0].addressLine1,
            data.getApplications.applications,
          ), */
        );
      }

      setInProgressApplications(data.getApplications.applications);
    } catch (e) {
      setErrorNotification(true);
    }
  });

  const getPendingApplicationData = (
    accountId: string,
    address: string,
    inProgressApps?: Application[],
  ) => {
    const apps = inProgressApps || inProgressApplications;
    // Applications with below statuses are returned in the getApplicatoins api response
    // but these should be ignored because they are not active applications
    const ignoreStatuses = [
      ApplicationStatus.Expired,
      ApplicationStatus.Submitted,
      ApplicationStatus.Canceled,
      ApplicationStatus.Rejected,
      ApplicationStatus.Completed,
    ];

    //Finding pending application by account and address line
    const foundApplicationByAddress = apps.filter(acc => {
      if (ignoreStatuses.includes(acc.status)) {
        return false;
      }

      const AccountInfoSection = (acc as ApplicationEr).details.find(
        detail => detail.sectionType === ApplicationErSectionType.ErAccountInfo,
      );

      let addressLine = '';

      if (AccountInfoSection) {
        addressLine =
          (AccountInfoSection as SectionErAccountInfo).data.address
            ?.addressLine1 || '';
      }

      return (
        acc.accountId === accountId && address.trim() === addressLine.trim()
      );
    });

    return foundApplicationByAddress;
  };

  const isAccountHasPendingApplication = (
    accountId: string,
    address: string,
    inProgressApps?: Application[],
  ) => {
    return (
      getPendingApplicationData(accountId, address, inProgressApps).length > 0
    );
  };

  // TODO: Revisit code after backend is ready
  const handleSelectedAccount = async (
    acc: AccountDetail,
    encryptedPremiseId: string,
    existingApplication: Application[],
  ) => {
    try {
      setIsLoading(true);
      // const hasExistingApplication = existingApplication.length > 0;
      // //If existing application found, fetch all the data and init form state
      // if (hasExistingApplication) {
      //   const { data: existingApplicationData } = await getApplicationDetails({
      //     applicationId: existingApplication?.[0]?.applicationNo,
      //   });
      //   updateFormState('account', acc);
      //   navigateToNextStep(
      //     existingApplicationData?.getApplication?.application as ApplicationEr,
      //   );
      //   return;
      // }

      const { data, errors } = await createApplication({
        personId: customer?.encryptedPersonId,
        premiseId: encryptedPremiseId,
        accountId: acc?.accountNumber,
      });

      if (errors) {
        throw new Error();
      }

      if (
        data?.createApplicationER?.ineligibilityReasons &&
        data?.createApplicationER?.ineligibilityReasons.length > 0
      ) {
        const ineligibilityReason =
          data.createApplicationER.ineligibilityReasons[0];

        switch (ineligibilityReason.code) {
          case IneligibilityReasonCode.IneligibleApplicationInProgress:
          case IneligibilityReasonCode.IneligibleApplicationLimit:
            void navigate(routes.PGE_PLUS_EV_CHARGERS_WELCOME_BACK, {
              state: { accountNumber: acc?.accountNumber, isIneligible: false },
            });
            break;
          case IneligibilityReasonCode.IneligibleMaxRebates:
            void navigate(routes.PGE_PLUS_REBATE_ONLY_INELIGIBLE, {
              state: {
                isIneligible: true,
                title: 'CHARGING_SUPER_STAR_PAGE_TITLE',
                message: 'REBATE_ONLY_INELIGIBLE_MAX_REBATES_1',
                showBackButton: false,
              },
            });
            break;
          case IneligibilityReasonCode.IneligibleC2M:
            void navigate(routes.PGE_PLUS_REBATE_ONLY_INELIGIBLE, {
              state: {
                isIneligible: true,
                title: 'EV_CHARGER_INELIGIBLE_TITLE',
                message:
                  ineligibilityReason.reference?.number ===
                    IneligibilityTypeNumber.PAST_DUE_OVER_TWO_MONTHS ||
                  ineligibilityReason.reference?.number ===
                    IneligibilityTypeNumber.PAYMENT_ARRANGEMENT
                    ? 'EV_CHARGER_INELIGIBLE_PAST_DUE_30_DAYS'
                    : 'REBATE_ONLY_NOT_ACTIVE',
                showBackButton: true,
              },
            });
            break;
          case IneligibilityReasonCode.IneligibleProductType:
            void navigate(routes.PGE_PLUS_REBATE_ONLY_INELIGIBLE, {
              state: {
                isIneligible: true,
                title: '',
                message: 'REBATE_ONLY_TIME_OF_THE_DAY',
                showBackButton: false,
              },
            });
            break;
          case IneligibilityReasonCode.IneligibleHomeType:
            void navigate(routes.PGE_PLUS_REBATE_ONLY_INELIGIBLE, {
              state: {
                isIneligible: true,
                title: 'FIDDLESTICKS_ALTERNATIVE',
                message: 'MULTIFAMILY_INELIGIBILITY',
                showBackButton: false,
              },
            });
            break;
          // case IneligibilityReasonCode.IneligibleApplicationLimit:
          //   void navigate(routes.PGE_PLUS_REBATE_ONLY_INELIGIBLE, {
          //     state: {
          //       isIneligible: true,
          //       title: '',
          //       message: 'REBATE_ONLY_INELIGIBLE_APPLICATION_IN_PROGRESS',
          //       showBackButton: false,
          //     },
          //   });
          //   break;
          default:
            void navigate(routes.PGE_PLUS_REBATE_ONLY_INELIGIBLE);
        }

        setIsLoading(false);
      } else if (data?.createApplicationER?.application) {
        //Update the selected account Form State
        updateFormState('account', acc);
        navigateToNextStep(data?.createApplicationER?.application);
      }
    } catch (e) {
      console.log(e);
      setIsLoading(false);
      setErrorNotification(true);
    }
  };

  const navigateToNextStep = (application: ApplicationEr) => {
    initFormStateAndNavigate(application);
    setIsLoading(false);
  };

  return (
    <StyledGrid>
      {(loading || isLoading || accountDetailsLoading) && (
        <Backdrop forceOpen />
      )}
      <Box display={'flex'}>
        <Typography
          component="h1"
          variant="h1"
          css={{
            textAlign: 'center',
            fontWeight: 700,
            lineHeight: '43px !important',
          }}
        >
          {content.get('REBATE_ONLY_SELECT_PREMISE_HEADER')}
        </Typography>
      </Box>
      <Box css={{ marginBottom: theme.spacing(12) }}>
        <Box css={{ marginTop: 30 }}>
          <Typography
            variant={'body1'}
            css={{
              fontSize: '20px !important',
              lineHeight: '26px !important',
              marginBottom: theme.spacing(3),
            }}
          >
            {content.get('REBATE_ONLY_SELECT_PREMISE_TEXT_CONFIRM_LOCATION')}
          </Typography>
          <Typography
            variant={'body1'}
            css={{
              fontSize: '20px !important',
              lineHeight: '26px !important',
            }}
          >
            {content.get('REBATE_ONLY_SELECT_PREMISE_TEXT_ADDRESS_SEARCH')}
          </Typography>
        </Box>
        <Box css={{ marginTop: theme.spacing(3) }}>
          <AccountSearch
            onSearch={(search: string) => {
              onAccountSearch(search);
            }}
            value={accountListParams.query}
            styleClass={classes.cardSearch}
            hideSearchIcon={true}
            maxLength={10}
          />
        </Box>
        {!loading && accountsList.length === 0 && accountListParams.query && (
          <Box>
            <Typography variant="body1" css={{ color: colors.roseRed }}>
              {content.get('NO_ACCOUNTS_FOUND_ERROR_TEXT')}
            </Typography>
          </Box>
        )}
        <Grid container direction="column">
          <Grid container item spacing={2}>
            {accountsList?.slice(0, showAccountByLimit).map(account => {
              return account?.premiseInfo?.map((premise, idx) => (
                <Grid item xs={12} md={6}>
                  <Box css={{ marginTop: theme.spacing(3) }} key={idx}>
                    <ProgramCard
                      key={`${account.accountNumber} - card`}
                      className={classes.accountCard}
                      title={premise?.addressLine1}
                      description={
                        <Fragment>
                          {content.get('ACCOUNT')}:{' '}
                          <strong>{account.accountNumber}</strong>
                          {isAccountHasPendingApplication(
                            account.accountNumber,
                            premise?.addressLine1!,
                          ) ? (
                            <FormHelperText error>
                              {content.get(
                                'EXISTING_APPLICATION_IN_PROGRESS_LABEL',
                              )}
                            </FormHelperText>
                          ) : null}
                        </Fragment>
                      }
                      onClick={() =>
                        handleSelectedAccount(
                          account,
                          premise?.encryptedPremiseId || '',
                          getPendingApplicationData(
                            account.accountNumber,
                            premise?.addressLine1!,
                          ),
                        )
                      }
                    />
                  </Box>
                </Grid>
              ));
            })}
          </Grid>
        </Grid>

        {accountsList?.length >= ITEMS_PER_PAGE &&
          accountsList?.length !== totalCount && (
            <Box css={{ marginTop: theme.spacing(2) }}>
              <Typography
                variant="subtitle1"
                component="div"
                className={classes.showMoreAccounts}
                onClick={() =>
                  setShowAccountByLimit(showAccountByLimit + ITEMS_PER_PAGE)
                }
              >
                {content.get('SHOW_MORE_ACCOUNTS')}
              </Typography>
            </Box>
          )}
      </Box>
    </StyledGrid>
  );
};

export default ChooseAccount;
